import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, Users, DollarSign, Mail, Phone, Activity,
  Search, Filter, Eye, MailIcon, Ban, ChevronRight,
  TrendingUp, Calendar, MoreVertical, RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SuperAdminDashboard = () => {
  const [overview, setOverview] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, [planFilter, statusFilter, sortBy, sortOrder]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [overviewRes, clientsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/super-admin/overview`, { headers }),
        axios.get(`${BACKEND_URL}/api/super-admin/clients`, { 
          headers,
          params: {
            plan: planFilter || undefined,
            status: statusFilter || undefined,
            search: searchQuery || undefined,
            sort: sortBy,
            order: sortOrder
          }
        })
      ]);

      setOverview(overviewRes.data);
      setClients(clientsRes.data.clients);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied. Super admin only.');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load admin data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchData();
  };

  const getPlanBadge = (plan) => {
    const styles = {
      free: 'bg-gray-500/20 text-gray-300',
      starter: 'bg-blue-500/20 text-blue-400',
      growth: 'bg-emerald-500/20 text-emerald-400',
      agency: 'bg-amber-500/20 text-amber-400'
    };
    return styles[plan] || styles.free;
  };

  const getStatusDot = (status) => {
    const colors = {
      active: 'bg-emerald-500',
      inactive_7d: 'bg-amber-500',
      inactive_30d: 'bg-red-500',
      never_logged_in: 'bg-gray-500'
    };
    return colors[status] || colors.inactive_30d;
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount / 100);
  };

  if (loading && !overview) {
    return (
      <div className="min-h-screen bg-[#0a0f1e] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div data-testid="super-admin-page" className="min-h-screen bg-[#0a0f1e] p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">AILeadMantra Admin</h1>
          <p className="text-[#94a3b8]">Platform Overview</p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={fetchData}
            variant="outline"
            className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-900/30"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button
            onClick={() => navigate('/super-admin/revenue')}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <DollarSign className="w-4 h-4 mr-2" />
            Revenue
          </Button>
        </div>
      </div>

      {/* Top Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="bg-[#1a2235] border-[#1e3a5f30]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <Building2 className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{overview?.total_workspaces || 0}</p>
                  <p className="text-xs text-[#94a3b8]">Total Clients</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
          <Card className="bg-[#1a2235] border-[#1e3a5f30]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-500/20 rounded-lg">
                  <Activity className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{overview?.active_today || 0}</p>
                  <p className="text-xs text-[#94a3b8]">Active Today</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card className="bg-[#1a2235] border-[#1e3a5f30]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-violet-500/20 rounded-lg">
                  <Users className="w-5 h-5 text-violet-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{overview?.total_leads || 0}</p>
                  <p className="text-xs text-[#94a3b8]">Total Leads</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
          <Card className="bg-[#1a2235] border-[#1e3a5f30]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-500/20 rounded-lg">
                  <DollarSign className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{formatCurrency(overview?.total_revenue_this_month || 0)}</p>
                  <p className="text-xs text-[#94a3b8]">Revenue MTD</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Card className="bg-[#1a2235] border-[#1e3a5f30]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-pink-500/20 rounded-lg">
                  <Mail className="w-5 h-5 text-pink-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{overview?.emails_sent_today || 0}</p>
                  <p className="text-xs text-[#94a3b8]">Emails Today</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
          <Card className="bg-[#1a2235] border-[#1e3a5f30]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-teal-500/20 rounded-lg">
                  <Phone className="w-5 h-5 text-teal-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{overview?.calls_made_today || 0}</p>
                  <p className="text-xs text-[#94a3b8]">Calls Today</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Clients Table */}
      <Card className="bg-[#1a2235] border-[#1e3a5f30]">
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <CardTitle className="text-white">All Clients</CardTitle>
            <div className="flex flex-wrap gap-3">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94a3b8]" />
                <input
                  type="text"
                  placeholder="Search business or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-10 pr-4 py-2 bg-[#0a0f1e] border border-[#1e3a5f] rounded-lg text-white placeholder-[#94a3b8] text-sm w-64"
                />
              </div>
              
              {/* Plan Filter */}
              <select
                value={planFilter}
                onChange={(e) => setPlanFilter(e.target.value)}
                className="px-3 py-2 bg-[#0a0f1e] border border-[#1e3a5f] rounded-lg text-white text-sm"
              >
                <option value="">All Plans</option>
                <option value="free">Free</option>
                <option value="starter">Starter</option>
                <option value="growth">Growth</option>
                <option value="agency">Agency</option>
              </select>
              
              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 bg-[#0a0f1e] border border-[#1e3a5f] rounded-lg text-white text-sm"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#1e3a5f30]">
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#94a3b8] uppercase">Business</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#94a3b8] uppercase">Plan</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#94a3b8] uppercase">Leads</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#94a3b8] uppercase">Emails</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#94a3b8] uppercase">Calls</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#94a3b8] uppercase">Joined</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#94a3b8] uppercase">Last Active</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#94a3b8] uppercase">Status</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#94a3b8] uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((client, index) => (
                  <motion.tr
                    key={client.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.02 }}
                    className="border-b border-[#1e3a5f30] hover:bg-[#0a0f1e]/50"
                  >
                    <td className="py-3 px-4">
                      <div>
                        <p className="text-white font-medium">{client.business_name}</p>
                        <p className="text-xs text-[#94a3b8]">{client.owner_email}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <Badge className={getPlanBadge(client.plan)}>
                        {client.plan || 'Free'}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-white">{client.leads_count}</td>
                    <td className="py-3 px-4 text-white">{client.emails_sent}</td>
                    <td className="py-3 px-4 text-white">{client.calls_made}</td>
                    <td className="py-3 px-4 text-[#94a3b8] text-sm">{formatDate(client.created_at)}</td>
                    <td className="py-3 px-4 text-[#94a3b8] text-sm">{formatDate(client.last_login)}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${getStatusDot(client.status)}`}></div>
                        <span className="text-[#94a3b8] text-sm capitalize">
                          {client.status?.replace('_', ' ')}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => navigate(`/super-admin/client/${client.id}`)}
                          className="p-2 hover:bg-[#1e3a5f] rounded-lg text-blue-400"
                          title="View"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => window.open(`mailto:${client.owner_email}`)}
                          className="p-2 hover:bg-[#1e3a5f] rounded-lg text-emerald-400"
                          title="Email"
                        >
                          <MailIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          {clients.length === 0 && (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-[#94a3b8] mx-auto mb-4" />
              <p className="text-[#94a3b8]">No clients found</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-8">
        <button
          onClick={() => navigate('/super-admin/revenue')}
          className="p-4 bg-[#1a2235] border border-[#1e3a5f30] rounded-xl hover:bg-[#1e3a5f20] transition-colors text-left"
        >
          <DollarSign className="w-6 h-6 text-amber-400 mb-2" />
          <h3 className="text-white font-medium">Revenue Dashboard</h3>
          <p className="text-xs text-[#94a3b8]">MRR, ARR, Growth</p>
        </button>
        
        <button
          onClick={() => navigate('/super-admin/health')}
          className="p-4 bg-[#1a2235] border border-[#1e3a5f30] rounded-xl hover:bg-[#1e3a5f20] transition-colors text-left"
        >
          <Activity className="w-6 h-6 text-emerald-400 mb-2" />
          <h3 className="text-white font-medium">Platform Health</h3>
          <p className="text-xs text-[#94a3b8]">Services, Jobs, Errors</p>
        </button>
        
        <button
          onClick={() => navigate('/super-admin/broadcast')}
          className="p-4 bg-[#1a2235] border border-[#1e3a5f30] rounded-xl hover:bg-[#1e3a5f20] transition-colors text-left"
        >
          <Mail className="w-6 h-6 text-blue-400 mb-2" />
          <h3 className="text-white font-medium">Broadcast Email</h3>
          <p className="text-xs text-[#94a3b8]">Message all clients</p>
        </button>
        
        <button
          onClick={() => navigate('/dashboard')}
          className="p-4 bg-[#1a2235] border border-[#1e3a5f30] rounded-xl hover:bg-[#1e3a5f20] transition-colors text-left"
        >
          <ChevronRight className="w-6 h-6 text-violet-400 mb-2" />
          <h3 className="text-white font-medium">Back to My CRM</h3>
          <p className="text-xs text-[#94a3b8]">Return to dashboard</p>
        </button>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
